import { createFormHook, createFormHookContexts, useStore } from "@tanstack/react-form";
import { Slot } from "radix-ui";
import { type ComponentProps, createContext, useContext, useId } from "react";
import { Field, FieldDescription, FieldError, FieldLabel } from "@/components/ui/field";

const { fieldContext, formContext, useFieldContext: useFormFieldContext, useFormContext } = createFormHookContexts();

type FormFieldContextValue = {
	id: string;
};

const FieldContext = createContext<FormFieldContextValue>({} as FormFieldContextValue);

const useFieldContext = <T = unknown>() => {
	const { id } = useContext(FieldContext);
	const { name, ...fieldContext } = useFormFieldContext<T>();
	if (!fieldContext) {
		throw new Error("useFieldContext should be used within <form.Field>");
	}

	return {
		id,
		name,
		formFieldId: `${id}-form-item`,
		formDescriptionId: `${id}-form-item-description`,
		formMessageId: `${id}-form-item-message`,
		...fieldContext,
	};
};

function FieldWrapper(props: ComponentProps<typeof Field>) {
	const id = useId();
	return (
		<FieldContext.Provider value={{ id }}>
			<Field {...props} />
		</FieldContext.Provider>
	);
}

function FieldControl({ ...props }: Slot.SlotProps) {
	const { formFieldId, formDescriptionId, formMessageId, store } = useFieldContext();

	const isInvalid = useStore(store, (state) => state.meta.isTouched && !state.meta.isValid);
	return (
		<Slot.Root
			aria-describedby={`${formDescriptionId} ${isInvalid ? formMessageId : ""}`}
			aria-invalid={isInvalid}
			data-slot="field-control"
			id={formFieldId}
			{...props}
		/>
	);
}

function FieldLabelWrapper({ ...props }: ComponentProps<typeof FieldLabel>) {
	const { formFieldId } = useFieldContext();
	return <FieldLabel htmlFor={formFieldId} {...props} />;
}

function FieldDescriptionWrapper(props: ComponentProps<typeof FieldDescription>) {
	const { formDescriptionId } = useFieldContext();
	return <FieldDescription id={formDescriptionId} {...props} />;
}

function FieldErrorWrapper({ errors: errorsProps, ...props }: ComponentProps<typeof FieldError>) {
	const { store, formMessageId } = useFieldContext();
	// eslint-disable-next-line @typescript-eslint/no-unsafe-return
	const _errors = useStore(store, (state) => state.meta.errors);
	const isInvalid = useStore(store, (state) => state.meta.isTouched && !state.meta.isValid);
	if (!isInvalid) return null;

	const errors = errorsProps ?? _errors;
	return <FieldError errors={errors} id={formMessageId} {...props} />;
}

const { useAppForm, withForm, useTypedAppFormContext } = createFormHook({
	fieldContext,
	formContext,
	fieldComponents: {
		Field: FieldWrapper,
		FieldLabel: FieldLabelWrapper,
		FieldDescription: FieldDescriptionWrapper,
		FieldError: FieldErrorWrapper,
		FieldControl,
	},
	formComponents: {},
});

export { useAppForm, useFieldContext, useFormContext, useTypedAppFormContext, withForm };
